import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { LogOut, User, ChevronDown, Moon, Sun, Menu, X, Shield } from 'lucide-react'
import { useToast } from '../ui/useToast'
import { adminNavigation } from './AdminSidebar'

export function AdminHeader() {
  const navigate = useNavigate()
  const { notify } = useToast()
  const [user, setUser] = useState<any>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isDark, setIsDark] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const fetchAdminSession = async () => {
      try {
        const res = await fetch('/api/admin/session', { credentials: 'include' })
        if (res.ok) {
          const data = await res.json()
          if (data.success && data.data?.user) {
            setUser(data.data.user)
          }
        }
      } catch {
        // Silent fail
      }
    }
    fetchAdminSession()
  }, [])

  useEffect(() => {
    if (isDark) document.documentElement.classList.add('dark')
    else document.documentElement.classList.remove('dark')
  }, [isDark])

  useEffect(() => {
    const handleClick = () => setIsDropdownOpen(false)
    if (isDropdownOpen) {
      document.addEventListener('click', handleClick)
      return () => document.removeEventListener('click', handleClick)
    }
  }, [isDropdownOpen])

  const handleLogout = async () => {
    try {
      await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'include',
      })
      notify({ kind: 'success', title: 'Logged out', message: 'Admin session ended.' })
      navigate('/admin/login')
    } catch (err: any) {
      notify({ kind: 'error', title: 'Logout failed', message: err.message })
    }
  }

  return (
    <header className="sticky top-0 z-40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl shadow-soft border-b border-gray-200 dark:border-gray-800">
      <div className="px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-2 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-500/10 text-gray-700 dark:text-gray-200 transition-all"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            <Link to="/admin/dashboard" className="flex items-center gap-2 group">
              <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div className="hidden sm:block">
                <div className="text-lg font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  Global Admin
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
                  System Administration
                </div>
              </div>
            </Link>

            <span className="hidden lg:inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full shadow-sm">
              <Shield className="h-3 w-3" />
              Super Admin
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2.5 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-500/10 text-gray-700 dark:text-gray-200 transition-all"
              aria-label="Toggle theme"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <Sun className="h-5 w-5 text-orange-500" />
              ) : (
                <Moon className="h-5 w-5 text-orange-600" />
              )}
            </button>

            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsDropdownOpen(!isDropdownOpen)
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-500/10 text-gray-700 dark:text-gray-200 transition-all"
              >
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt="Avatar"
                    className="h-7 w-7 rounded-full object-cover ring-2 ring-orange-500/20"
                  />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-orange-400 to-red-400 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                )}
                <span className="hidden md:block font-medium text-sm">{user?.name}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-900 rounded-2xl shadow-soft-lg border border-gray-200 dark:border-gray-800 overflow-hidden animate-scale-in">
                  <div className="px-4 py-3 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 border-b border-gray-200 dark:border-gray-800">
                    <div className="font-semibold text-gray-900 dark:text-gray-100">{user?.name}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">{user?.email}</div>
                    <div className="mt-1 inline-block">
                      <span className="px-2 py-0.5 text-xs font-semibold bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full">
                        {user?.role}
                      </span>
                    </div>
                  </div>

                  <div className="py-1">
                    <button
                      onClick={() => {
                        setIsDropdownOpen(false)
                        handleLogout()
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-500/10 transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden animate-fade-in"
            onClick={() => setMobileOpen(false)}
          />

          <div className="fixed left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white dark:bg-gray-900 shadow-soft-lg z-50 md:hidden animate-slide-down">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <span className="text-lg font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Admin Menu</span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="p-4 space-y-1 custom-scrollbar overflow-y-auto" style={{ maxHeight: 'calc(100vh - 80px)' }}>
              {adminNavigation.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-orange-50 dark:hover:bg-orange-500/10 transition-all font-medium"
                  >
                    <Icon className="h-5 w-5 text-orange-500" />
                    {item.name}
                  </Link>
                )
              })}
            </nav>
          </div>
        </>
      )}
    </header>
  )
}
